import { CSSProperties } from 'react';

import { COPYTEXT, DISMISS, HOTSPOT, OVERLAY, TOUR_STEP } from '@candu-ai/styleguide/selectors';
import { MediaBreakpoint } from 'craft/types/media';

export enum CssSelectorType {
  HTML = 'HTML',
  ID = 'Id',
  Subclass = 'Subclass',
  Root = 'Root',
}

const endBasepoints = Object.values(MediaBreakpoint).map((t) => `-${t}`);

export const getSelectorViewport = (id: CssSelectorId): string | undefined => {
  return endBasepoints.find((e) => id.endsWith(e))?.replace('-', '');
};
export const isBaseSelectorId = (id: CssSelectorId): boolean => {
  return !endBasepoints.find((e) => id.endsWith(e));
};

export const getBaseSelectorId = (id: CssSelectorId): CssSelectorId => {
  if (isBaseSelectorId(id)) {
    return id;
  }
  const spaces = id.split('-');
  spaces.pop();
  return spaces.join('-') as CssSelectorId;
};
export const getResponsiveSelectorIds = (id: CssSelectorId) => ({
  [MediaBreakpoint.LG]: `${id}-${MediaBreakpoint.LG}` as CssSelectorId,
  [MediaBreakpoint.MD]: `${id}-${MediaBreakpoint.MD}` as CssSelectorId,
  [MediaBreakpoint.SM]: `${id}-${MediaBreakpoint.SM}` as CssSelectorId,
});

export enum CssSelectorId {
  // Hotspot
  HotspotArrow = 'hotspot-arrow',
  HotspotBeacon = 'hotspot-beacon',
  HotspotBeaconSVG = 'hotspot-beacon-svg',
  HotspotWrapper = 'hotspot-wrapper',
  HotspotContainer = 'hotspot-container',
  HotspotBody = 'hotspot-body',
  HotspotDismiss = 'hotspot-dismiss',
  HotspotDismissSVG = 'hotspot-dismiss-svg',

  // Tour step
  TourStepArrow = 'tour-step-arrow',
  TourStepWrapper = 'tour-step-wrapper',
  TourStepContainer = 'tour-step-container',
  TourStepBody = 'tour-step-body',
  TourDismiss = 'tour-dismiss',
  TourDismissSVG = 'tour-dismiss-svg',
  TourStepBackdropWrapper = 'tour-step-backdrop-wrapper',
  TourStepBackdropContainer = 'tour-step-backdrop-container',

  // Overlay
  OverlayDismiss = 'overlay-dismiss',
  OverlayDismissSVG = 'overlay-dismiss-svg',

  // Card
  CardDismiss = 'card-dismiss',
  CardDismissSVG = 'card-dismiss-svg',

  // Dismiss Icon
  Dismiss = 'dismiss-wrapper',
  DismissSVG = 'dismiss-svg',

  // Modal
  ModalBackdrop = 'modal-backdrop',
  ModalContainer = 'modal-container',

  // Root generic styles
  Root = 'root',
  // commonly accessed viewports
  RootSM = 'root-sm',
  RootMD = 'root-md',
  RootLG = 'root-lg',

  FormError = 'form-error',
  FormLabel = 'form-label',
  FormTextInput = 'form-text-input',
  FormTextArea = 'form-text-area',
  FormSelectInput = 'form-select-input',
  FormCheckboxListDiv = 'form-checkbox-list-div',

  // Typography
  TypographyHrefChildren = 'typography-href',

  // RichText
  CopyText = 'copy-text-wrapper',
  CopyTextSVG = 'copy-text-svg',
}

export const getCssSelectorDefinition = (cssSelectorId: CssSelectorId) => {
  if (isBaseSelectorId(cssSelectorId)) {
    // @ts-expect-error
    return CssSelectorDefinitions[cssSelectorId];
  }
  const baseSelector = getBaseSelectorId(cssSelectorId);
  const selectorViewport = getSelectorViewport(cssSelectorId);
  // @ts-expect-error
  const baseSelectorDefinition = CssSelectorDefinitions[baseSelector];
  return {
    ...baseSelectorDefinition,
    viewport: selectorViewport,
  };
};

export const CssSelectorDefinitions = {
  [CssSelectorId.Root]: {
    type: CssSelectorType.Root,
  },
  [CssSelectorId.TypographyHrefChildren]: {
    type: CssSelectorType.HTML,
    selector: '> a',
  },
  // Modals
  [CssSelectorId.ModalContainer]: {
    type: CssSelectorType.HTML,
    selector: `.${OVERLAY.Container}`,
  },
  [CssSelectorId.ModalBackdrop]: {
    type: CssSelectorType.HTML,
    selector: `.${OVERLAY.Backdrop}`,
  },
  [CssSelectorId.OverlayDismiss]: {
    type: CssSelectorType.HTML,
    selector: `> .${OVERLAY.Container} > .${DISMISS.Wrapper}`,
  },
  [CssSelectorId.OverlayDismissSVG]: {
    type: CssSelectorType.HTML,
    selector: `> .${OVERLAY.Container} > .${DISMISS.Wrapper} .${DISMISS.SVG}`,
  },
  // Forms
  [CssSelectorId.FormLabel]: {
    type: CssSelectorType.HTML,
    selector: `.candu-form--form-label`,
  },
  [CssSelectorId.FormError]: {
    type: CssSelectorType.HTML,
    selector: `.error`,
  },
  [CssSelectorId.FormTextInput]: {
    type: CssSelectorType.HTML,
    selector: 'input.candu-form--input-field',
  },
  [CssSelectorId.FormTextArea]: {
    type: CssSelectorType.HTML,
    selector: 'textarea.candu-form--input-field',
  },
  [CssSelectorId.FormSelectInput]: {
    type: CssSelectorType.HTML,
    selector: 'select.candu-form--input-field',
  },
  [CssSelectorId.FormCheckboxListDiv]: {
    type: CssSelectorType.HTML,
    selector: '.candu-form--checkbox-div',
  },
  // Hotspot
  [CssSelectorId.HotspotBeacon]: {
    type: CssSelectorType.HTML,
    selector: `.${HOTSPOT.Beacon}`,
  },
  [CssSelectorId.HotspotBeaconSVG]: {
    type: CssSelectorType.HTML,
    selector: `.${HOTSPOT.Beacon} svg`,
  },
  [CssSelectorId.HotspotWrapper]: {
    type: CssSelectorType.HTML,
    selector: `> .${HOTSPOT.Wrapper}`,
  },
  [CssSelectorId.HotspotArrow]: {
    type: CssSelectorType.HTML,
    selector: `> .${HOTSPOT.Wrapper} .${HOTSPOT.Arrow}`,
  },
  [CssSelectorId.HotspotContainer]: {
    type: CssSelectorType.HTML,
    selector: `> .${HOTSPOT.Wrapper} .${HOTSPOT.Container}`,
  },
  [CssSelectorId.HotspotBody]: {
    type: CssSelectorType.HTML,
    selector: `> .${HOTSPOT.Wrapper} .${HOTSPOT.Body}`,
  },
  [CssSelectorId.HotspotDismiss]: {
    type: CssSelectorType.HTML,
    selector: `> .${HOTSPOT.Wrapper} .${HOTSPOT.Container} > .${DISMISS.Wrapper}`,
  },
  [CssSelectorId.HotspotDismissSVG]: {
    type: CssSelectorType.HTML,
    selector: `> .${HOTSPOT.Wrapper} .${HOTSPOT.Container} > .${DISMISS.Wrapper} .${DISMISS.SVG}`,
  },
  // Tour step
  [CssSelectorId.TourStepBackdropContainer]: {
    type: CssSelectorType.HTML,
    selector: `.${TOUR_STEP.BackdropContainer}`,
  },
  [CssSelectorId.TourStepBackdropWrapper]: {
    type: CssSelectorType.HTML,
    selector: `.${TOUR_STEP.BackdropWrapper}`,
  },
  [CssSelectorId.TourStepWrapper]: {
    type: CssSelectorType.HTML,
    selector: `> .${TOUR_STEP.Wrapper}`,
  },
  [CssSelectorId.TourStepArrow]: {
    type: CssSelectorType.HTML,
    selector: `> .${TOUR_STEP.Wrapper} .${TOUR_STEP.Arrow}`,
  },
  [CssSelectorId.TourStepContainer]: {
    type: CssSelectorType.HTML,
    selector: `> .${TOUR_STEP.Wrapper} .${TOUR_STEP.Container}`,
  },
  [CssSelectorId.TourStepBody]: {
    type: CssSelectorType.HTML,
    selector: `> .${TOUR_STEP.Wrapper} .${TOUR_STEP.Body}`,
  },
  [CssSelectorId.TourDismiss]: {
    type: CssSelectorType.HTML,
    selector: `> .${TOUR_STEP.Wrapper} .${TOUR_STEP.Container} > .${DISMISS.Wrapper}`,
  },
  [CssSelectorId.TourDismissSVG]: {
    type: CssSelectorType.HTML,
    selector: `> .${TOUR_STEP.Wrapper} .${TOUR_STEP.Container} > .${DISMISS.Wrapper} .${DISMISS.SVG}`,
  },
  // Card
  [CssSelectorId.CardDismiss]: {
    type: CssSelectorType.HTML,
    selector: `> .${DISMISS.Wrapper}`,
  },
  [CssSelectorId.CardDismissSVG]: {
    type: CssSelectorType.HTML,
    selector: `> .${DISMISS.Wrapper} .${DISMISS.SVG}`,
  },
  // RichText
  [CssSelectorId.CopyText]: {
    type: CssSelectorType.HTML,
    selector: `.${COPYTEXT.Wrapper}`,
  },
  [CssSelectorId.CopyTextSVG]: {
    type: CssSelectorType.HTML,
    selector: `.${COPYTEXT.Wrapper} .${COPYTEXT.SVG}`,
  },
};

export interface CssSelector {
  type: CssSelectorType;
  css: CSSProperties;
  viewport?: MediaBreakpoint;
  selector?: string;
  priority?: string;
}
